/* global HTMLDivElement, HTMLElement, KeyboardEvent */
/**
 * search-modal.tsx
 * Stateful command-palette shell: owns query state, keyboard handling, the
 * scroll container, and the activate dispatcher. Builds palette groups from
 * api state, then delegates row/group rendering to SearchPalette
 * (search-palette.tsx). Row/group types + static config live there too.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFocusTrap } from "../lib/focus-trap";
import { Search, X } from "lucide-react";
import { CheckCircle, BookOpen, FolderKanban, Goal, Wallet, Flame, Inbox, Calendar, BookMarked } from "lucide-react";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { EntityKind } from "./entity-form";
import {
  CREATE_DEFS, NAV_DEFS, flatRows, SearchPalette,
  type Hit, type Group, type PaletteRow,
} from "./search-palette";

// ── Component ─────────────────────────────────────────────────────────────────

export function SearchModal({
  api,
  onClose,
  onGoto,
  onForm,
}: {
  api: LifeOsApi;
  onClose: () => void;
  onGoto: (v: string) => void;
  onForm: (kind: EntityKind, item?: Record<string, unknown>) => void;
}) {
  const [q, setQ] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, true);

  // Build searchable entity hits from current api state
  const allHits = useMemo<Hit[]>(() => [
    // ── Core entities (original) ──────────────────────────────────────────────
    ...api.state.tasks.map((t) => ({ id: t.id, view: "tasks",    type: "Việc",       title: t.title,    detail: t.status,                                                        icon: CheckCircle })),
    ...api.state.notes.map((n) => ({ id: n.id, view: "notes",    type: "Ghi chú",    title: n.title,    detail: n.tags.map((x) => `#${x}`).join(" ") || n.body.slice(0, 40),    icon: BookOpen    })),
    ...api.state.projects.map((p) => ({ id: p.id, view: "projects", type: "Dự án",  title: p.name,     detail: p.area,                                                          icon: FolderKanban})),
    ...api.state.goals.map((g) => ({ id: g.id, view: "goals",    type: "Mục tiêu",   title: g.title,    detail: g.metric,                                                        icon: Goal        })),
    ...api.state.transactions.map((t) => ({ id: t.id, view: "finance", type: "Giao dịch", title: t.category, detail: `${t.type === "income" ? "+" : "−"}${t.amount.toLocaleString("vi-VN")}₫`, icon: Wallet })),
    // ── Extended entities ─────────────────────────────────────────────────────
    // Habits: name is the primary search text; detail shows weekly target
    ...api.state.habits.map((h) => ({ id: h.id, view: "habits",  type: "Thói quen",  title: h.name,     detail: `${h.targetPerWeek}×/tuần`,                                      icon: Flame       })),
    // Inbox items: text is the capture content; hint is optional context
    ...api.state.inbox.map((i) => ({ id: i.id, view: "inbox",    type: "Inbox",      title: i.text,     detail: i.hint ?? (i.status ?? "inbox"),                                 icon: Inbox       })),
    // Calendar events: title + date/time as detail
    ...api.state.events.map((e) => ({ id: e.id, view: "calendar", type: "Lịch",      title: e.title,    detail: `${e.date}${e.time ? " " + e.time : ""}`,                        icon: Calendar    })),
    // Daily plans: no text body — use date as title; MITs count as detail
    ...api.state.dailyPlans.map((p) => ({ id: p.id, view: "journal", type: "Kế hoạch ngày", title: `Kế hoạch ${p.date}`, detail: `${p.mitTaskIds.length} việc quan trọng`,      icon: BookMarked  })),
    // Daily reviews: reflection text is the key searchable content
    ...api.state.dailyReviews.map((r) => ({ id: r.id, view: "journal", type: "Tổng kết ngày", title: `Tổng kết ${r.date}`, detail: r.reflection.slice(0, 60),                   icon: BookMarked  })),
    // Weekly/monthly reviews: reflection + optional wins text
    ...api.state.weeklyReviews.map((w) => ({ id: w.id, view: "journal", type: w.scope === "week" ? "Tổng kết tuần" : "Tổng kết tháng", title: `Tổng kết ${w.date}`, detail: (w.wins ?? w.reflection).slice(0, 60), icon: BookMarked })),
    // Budgets: category is the primary name; detail shows monthly limit
    ...api.state.budgets.map((b) => ({ id: b.id, view: "finance", type: "Ngân sách", title: b.category, detail: `${b.monthlyLimit.toLocaleString("vi-VN")}₫/tháng`,             icon: Wallet      })),
  ], [api.state]);

  const term = q.trim().toLowerCase();

  // Build palette groups based on query
  const groups = useMemo<Group[]>(() => {
    if (term === "") return [];

    const result: Group[] = [];

    // Group 1: "Tạo nhanh" — create actions matching query
    const createRows: PaletteRow[] = CREATE_DEFS
      .filter((c) => c.label.toLowerCase().includes(term) || term.length > 0)
      .map((c): PaletteRow => ({
        kind: "create",
        entityKind: c.entityKind,
        label: c.label,
        icon: c.icon,
        title: `${c.prefix}: ${q.trim()}`,
      }));
    if (createRows.length) result.push({ heading: "Tạo nhanh", rows: createRows });

    // Group 2: "Đi tới" — nav views matching query
    const navRows: PaletteRow[] = NAV_DEFS
      .filter((n) => n.label.toLowerCase().includes(term))
      .map((n): PaletteRow => ({ kind: "nav", view: n.view, label: n.label, icon: n.icon }));
    if (navRows.length) result.push({ heading: "Đi tới", rows: navRows });

    // Group 3: Entity search results
    const hits = allHits
      .filter((h) => `${h.title} ${h.detail} ${h.type}`.toLowerCase().includes(term))
      .slice(0, 12);
    if (hits.length) {
      result.push({ heading: "Kết quả", rows: hits.map((h): PaletteRow => ({ kind: "entity", data: h })) });
    }

    return result;
  }, [term, q, allHits]);

  const flat = useMemo(() => flatRows(groups), [groups]);
  const total = flat.length;

  // Reset active index when groups change
  useEffect(() => { setActiveIdx(0); }, [term]);

  // Activate a row
  const activate = useCallback((row: PaletteRow) => {
    if (row.kind === "entity") { onGoto(row.data.view); onClose(); }
    else if (row.kind === "nav") { onGoto(row.view); onClose(); }
    else {
      // Open prefilled form — KISS: pass title in item so form can pre-fill it
      onForm(row.entityKind, { title: q.trim() });
      onClose();
    }
  }, [onGoto, onClose, onForm, q]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, total - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && total > 0) {
      e.preventDefault();
      activate(flat[activeIdx]);
    } else if (e.key === "Escape") {
      onClose();
    }
  }, [total, flat, activeIdx, activate, onClose]);

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-active="true"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  // Global Escape listener so closing works even if focus leaves the input
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[70] flex justify-center bg-ink-900/40 p-4 pt-[12vh] backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Tìm kiếm"
    >
      <div
        ref={panelRef}
        className="fade-in h-fit w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-ink-200 px-4 py-3.5">
          <Search className="h-5 w-5 text-ink-400" />
          <input
            ref={inputRef}
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent text-[16px] outline-none"
            placeholder="Tìm, tạo nhanh hoặc đi tới…"
            aria-label="Tìm kiếm"
          />
          <button
            onClick={onClose}
            className="grid h-7 w-7 place-items-center rounded-lg text-ink-400 hover:bg-ink-200/60"
            aria-label="Đóng"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[56vh] overflow-auto p-2">
          {term === "" && (
            <p className="px-3 py-6 text-center text-[13px] text-ink-400">
              Gõ để tìm, tạo nhanh hoặc chuyển đến màn hình
            </p>
          )}
          {term !== "" && groups.length === 0 && (
            <p className="px-3 py-6 text-center text-[13px] text-ink-400">
              Không tìm thấy &ldquo;{q}&rdquo;.
            </p>
          )}

          <SearchPalette groups={groups} activeIdx={activeIdx} onActivate={activate} />
        </div>

        {/* Footer hint */}
        {term !== "" && total > 0 && (
          <div className="border-t border-ink-100 px-4 py-2 text-[11px] text-ink-400">
            ↑↓ di chuyển · Enter chọn · Esc đóng
          </div>
        )}
      </div>
    </div>
  );
}
